/**
 * Auto-Shift Generator
 * Creates shifts from recurring schedules on app load.
 * Checks for duplicates to avoid creating shifts that already exist.
 */

import { format } from 'date-fns';
import { getRecurringSchedules, getShifts, saveShift, getClients } from './storageUtils';
import { calculateShiftTotal } from './ndisUtils';

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * Check if two time ranges overlap.
 * Times are "HH:MM" strings.
 */
function timesOverlap(start1, end1, start2, end2) {
  if (!start1 || !end1 || !start2 || !end2) return false;
  const toMin = (t) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  const s1 = toMin(start1), e1 = toMin(end1);
  const s2 = toMin(start2), e2 = toMin(end2);
  return s1 < e2 && s2 < e1;
}

/**
 * Check if a shift already exists for this client on this date
 * with overlapping times (to prevent duplicates).
 */
function shiftAlreadyExists(existingShifts, clientId, dateStr, startTime, endTime) {
  return existingShifts.some(shift => {
    if (shift.clientId !== clientId) return false;
    // Normalize date comparison - handle both Date objects and strings
    let shiftDate = shift.date;
    if (shiftDate instanceof Date) {
      // Convert Date object to YYYY-MM-DD string
      shiftDate = format(shiftDate, 'yyyy-MM-dd');
    } else if (typeof shiftDate === 'string') {
      // Extract only the date part if it's an ISO string
      shiftDate = shiftDate.split('T')[0];
    }
    if (shiftDate !== dateStr) return false;
    // If either shift has no times, match on client+date alone
    if (!startTime || !endTime || !shift.startTime || !shift.endTime) return true;
    return timesOverlap(startTime, endTime, shift.startTime, shift.endTime);
  });
}

/**
 * Generate shifts from recurring schedules for a given date.
 * Returns the number of shifts created.
 */
function generateShiftsForDate(targetDate, schedules, existingShifts, clients) {
  const dateStr = format(targetDate, 'yyyy-MM-dd');
  const dayKey = DAY_KEYS[targetDate.getDay()];

  let created = 0;

  for (const schedule of schedules) {
    const clientId = schedule.clientId;
    if (!clientId) continue;

    const client = clients.find(c => c.id === clientId);
    // Skip schedules for clients that don't exist in current user's data
    if (!client) continue;

    // Collect all shifts for this date from this schedule
    const dayShifts = [];

    // Check dateSchedule format first (new format: date-keyed)
    if (schedule.dateSchedule && schedule.dateSchedule[dateStr]) {
      const ds = schedule.dateSchedule[dateStr];
      if (Array.isArray(ds)) {
        dayShifts.push(...ds);
      } else if (ds && ds.duration) {
        dayShifts.push(ds);
      }
    }

    // Also check blocks format (old format: day-of-week based)
    if (schedule.blocks && dayShifts.length === 0) {
      for (const block of schedule.blocks) {
        const dayData = block.days?.[dayKey];
        if (dayData && dayData.duration) {
          dayShifts.push(dayData);
        }
      }
    }

    // Create shifts for each roster entry
    for (const rosterShift of dayShifts) {
      const startTime = rosterShift.startTime || null;
      const endTime = rosterShift.endTime || null;
      const duration = parseFloat(rosterShift.duration) || 0;

      if (duration <= 0) continue;

      // Check for duplicates
      if (shiftAlreadyExists(existingShifts, clientId, dateStr, startTime, endTime)) {
        continue;
      }

      // Calculate hours from times or use duration
      let hours = duration;
      if (startTime && endTime) {
        const [sh, sm] = startTime.split(':').map(Number);
        const [eh, em] = endTime.split(':').map(Number);
        const diffMin = (eh * 60 + em) - (sh * 60 + sm);
        if (diffMin > 0) hours = parseFloat((diffMin / 60).toFixed(2));
      }

      const hourlyRate = parseFloat(rosterShift.unit) || (client?.defaultRate ? parseFloat(client.defaultRate) : 0);
      const costs = calculateShiftTotal(hours, hourlyRate, 0);

      const newShift = {
        clientId,
        date: dateStr,
        startTime: startTime || '',
        endTime: endTime || '',
        hours,
        ndisItemCode: rosterShift.serviceItemId || '',
        serviceDescription: rosterShift.serviceItemName || '',
        hourlyRate,
        kilometers: 0,
        notes: '',
        status: 'completed',
        serviceTotal: costs.serviceTotal,
        transportTotal: costs.transportTotal,
        total: costs.total,
        autoGenerated: true,
      };

      const savedShift = saveShift(newShift);
      existingShifts.push(savedShift);
      created++;
    }
  }

  return created;
}

/**
 * Generate shifts from recurring schedules for today + catch up missed days.
 * Checks last 7 days to fill any gaps.
 * Returns total number of shifts created.
 */
export function generateTodaysShifts() {
  const schedules = getRecurringSchedules();
  const existingShifts = getShifts();
  const clients = getClients();

  let totalCreated = 0;
  const today = new Date();

  // Generate for today + past 7 days (catch-up)
  for (let i = 0; i <= 7; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    totalCreated += generateShiftsForDate(date, schedules, existingShifts, clients);
  }

  return totalCreated;
}
